import clsx from 'clsx';
import React from 'react';
import FormHelperText from '../../atoms/FormHelperText/FormHelperText';
import FormValidationMessage from '../../atoms/FormValidationMessage/FormValidationMessage';
import useFormFieldAria from '../../hooks/useFormFieldAria';

type FormFieldProps = {
  children: React.ReactElement;
  className?: string;
  error?: boolean;
  errorMessage?: string;
  helperText?: string;
  id?: string;
  isDisabled?: boolean;
  /**
   * Visually hides the `<label>` while keeping it in the DOM for screen readers.
   * Only takes effect when `label` is provided; ignored otherwise.
   */
  isLabelHidden?: boolean;
  isRequired?: boolean;
  /**
   * Visible label text. When the visual design hides the label (e.g. a search bar
   * with a magnifying-glass icon), prefer `label="..." + isLabelHidden` so the
   * `<label>` semantic is preserved in the DOM. Omit this prop only as a last
   * resort, in edge cases where no semantic label fits at all; in that case the
   * `<label>` element is not rendered and the consumer must provide the
   * accessible name on the child by other means.
   */
  label?: string;
  success?: boolean;
  successMessage?: string;
};

function FormField({
  children,
  className,
  error = false,
  errorMessage,
  helperText,
  id,
  isDisabled = false,
  isLabelHidden = false,
  isRequired = false,
  label,
  success = false,
  successMessage,
}: FormFieldProps): JSX.Element {
  const { ariaDescribedBy, ids } = useFormFieldAria({
    error,
    errorMessage,
    helperText,
    id,
    success,
    successMessage,
  });

  const shouldShowError = error && !!errorMessage;
  const shouldShowSuccess = !error && success && !!successMessage;

  // `validationState` is purely decorative: triggers the inline icon inside the child input.
  const validationState = shouldShowError ? 'error' : shouldShowSuccess ? 'success' : undefined;

  const enhancedChild = React.cloneElement(children, {
    id: ids.baseId,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': error || undefined,
    'aria-required': isRequired || undefined,
    disabled: isDisabled || children.props.disabled,
    ...(validationState && { validationState }),
  });

  return (
    <div
      className={clsx(
        'm-form-field',
        className,
        { 'm-form-field--disabled': isDisabled },
      )}
    >
      {label && (
        <label
          id={ids.labelId}
          htmlFor={ids.baseId}
          className={clsx('m-form-field__label', { 'm-form-field__label--hidden': isLabelHidden })}
        >
          {label} {isRequired && <sup aria-hidden="true">*</sup>}
        </label>
      )}

      {enhancedChild}

      {helperText && <FormHelperText id={ids.helperId} text={helperText} />}

      {shouldShowError && (
        <FormValidationMessage id={ids.validationId} type="error" text={errorMessage} />
      )}

      {shouldShowSuccess && (
        <FormValidationMessage id={ids.validationId} type="success" text={successMessage} />
      )}
    </div>
  );
}

export default FormField;
